import type { StorageResourceRef } from '../../../../../types/azureStorage';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { nonMutatingSasLetters, SAS_PERMISSION_ORDER, sasResourceKindOf, type StorageSasResourceKind } from '../../../../../core/engines/azureStorageSasParameters';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import { sasPermissionName } from '../../../../../core/engines/azureStorageSasSummary';

/**
 * The letter alphabet of the signed / policy-bearing resource (`sasResourceKindOf`): S7 share `rcwdl` / file `rcwd`,
 * S8 a Data Lake directory `racwdlmeop`. Refs with no SAS / policy of their own (the dialogs refuse them first) fall back
 * to the blob alphabet.
 */
const kind = (resource: StorageResourceRef): StorageSasResourceKind => sasResourceKindOf(resource) ?? 'blob';

/** UI/UX fix round A (U19): one reading order on every SAS kind — read, add, create, write (a table's update), delete, list, then the rest. */
const READING_ORDER = 'racwudl';
export function permissionDisplayOrder(letters: string): string[] {
  const first = [...READING_ORDER].filter((letter) => letters.includes(letter));
  return [...first, ...[...letters].filter((letter) => !READING_ORDER.includes(letter))];
}

/**
 * One checkbox per letter the resource accepts, shown in the one reading order (`permissionDisplayOrder`, U19); the value
 * is always emitted in the SDK's canonical order (a stored access policy must be saved canonical — Fable F7). Every
 * checkbox reads the permission's name with its Azure letter secondary (A12 / U20: `compact` — the policy rows — used to
 * show letters only); `compact` keeps a denser wrapping grid. `disabled` locks every letter with the read-only reason
 * (the policies dialog's view-only mode); `governed` shows a stored policy's own permissions, ticked and locked (A1).
 */
export function SasPermissionFields(props: { resource: StorageResourceRef; value: string; onChange: (value: string) => void; readOnly: boolean; governed: boolean; compact?: boolean; disabled?: boolean; legend?: string }) {
  const resourceKind = kind(props.resource);
  const readOnlyLetters = nonMutatingSasLetters(resourceKind);
  const letters = SAS_PERMISSION_ORDER[resourceKind];
  const toggle = (letter: string, checked: boolean) => {
    const next = checked ? `${props.value}${letter}` : props.value.split(letter).join('');
    props.onChange([...letters].filter((candidate) => next.includes(candidate)).join(''));
  };
  return <fieldset className="space-y-1"><legend className="text-sm font-medium">{props.legend ?? 'Permissions'}</legend><div className={props.compact ? 'grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-x-3 gap-y-1' : 'grid grid-cols-2 gap-1'}>
    {permissionDisplayOrder(letters).map((letter) => {
      const name = sasPermissionName(resourceKind, letter);
      const refused = props.disabled || (props.readOnly && !readOnlyLetters.includes(letter));
      const title = refused ? STORAGE_READ_ONLY_REASON : props.governed ? 'Governed by the policy' : undefined;
      return <label key={letter} className="flex items-center gap-1 text-xs" title={title}>
        {/* C1 (N27): the shared Checkbox, as in the ACL editor (native boxes here, shadcn there, looked like two controls). */}
        <Checkbox aria-label={`${name} (${letter})`} title={title} disabled={refused || props.governed} checked={props.value.includes(letter)} onCheckedChange={(checked) => toggle(letter, checked === true)} />
        <span>{name}</span> <span className="text-muted-foreground">({letter})</span>
      </label>;
    })}
  </div></fieldset>;
}
